import React, {useState, ChangeEvent, useCallback, useEffect, useRef} from 'react';
import styled from "styled-components";
import VideoZoomButton from "./VideoZoomButton";
import DEFAULT_VIDEO_IMG from "../../assets/defaultImg/default_video_image.svg";

const OFFSET_TOP = 17;
const OFFSET_LEFT = 13;

const StyledRootDiv = styled.div`
    position:relative;
`;

const StyledVideoFrame = styled.div`
    position: absolute;
    -webkit-transition:width 1s, height 1s, -webkit-transform 1s;
    transition:width 1s, height 1s, transform 1s;
    z-index: 10;
    ${
    (props) => (props as any)['data-zoom'] ?
        zoomInStyle :
        zoomOutStyle
}
    ${
    (props) => (props as any)['data-hidden'] && 'visibility: hidden'
}
`;

const Blind = styled.div`
    background-color: #D43136;
    color: white;
    width: 100%;
    height: 100%;
    font-weight: bold;
    font-size: 26px;
    span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const zoomInStyle = `
    width: 640px;
    box-shadow: 0px 0px 40px #00000076;
    transform: translate(${OFFSET_LEFT}px, ${OFFSET_TOP}px);
`;

const zoomOutStyle = `
    width: 100%;
`;

const StyledVideoZoomButton = styled.div`
    position: absolute;
    right: 5px;
    bottom: 4px;
`;

const StyledVideo = styled.video`
    position: relative;
    left: 0;
    width: 100%;
    z-index: 10;
    -webkit-transition:width 1s, height 1s, -webkit-transform 2s;
    transition:width 1s, height 1s, transform 2s;
`;

const StyledDefaultImage = styled.img`
    width: 100%;
    height: 100%;
`;
interface IVideoPreviewProps {
    className?: string;
    onTimeUpdate?: (ms: number) => void;
    onDurationChange?: (ms: number) => void;
    isBlind?: boolean
    sourceSrc: string;
    sourceType: string;
}

const VideoPreview = React.forwardRef<HTMLVideoElement, IVideoPreviewProps>(({
                                                                                 className,
                                                                                 onTimeUpdate,
                                                                                 onDurationChange,
                                                                                 isBlind,
                                                                                 sourceSrc,
                                                                                 sourceType
                                                                             }, ref) => {

    let clickX: number = 0;
    let clickY: number = 0;
    let mouseDown: boolean = false;

    const refVideoFrame = useRef<HTMLDivElement>(null);
    const [windowSize, setWindowSize] = useState<{ width: number, height: number }>({width: 0, height: 0});
    const [zoom, setZoom] = useState<boolean>(false);
    const [videoX, setVideoX] = useState<number>(0);
    const [videoY, setVideoY] = useState<number>(0);
    const [vidoeError, setVideoError] = useState<boolean>(false);
    useEffect(() => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setVideoError(false);
    }, [sourceSrc]);

    useEffect(() => {
        if (zoom) {
            moveVideoFrame(videoX, videoY);
        }
    }, [windowSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
    };

    const handleTimeUpdate = useCallback((e: ChangeEvent<HTMLVideoElement>) => {
        onTimeUpdate?.(e.currentTarget.currentTime * 1000);
    }, [onTimeUpdate]);

    const handleDurationChange = useCallback((e: ChangeEvent<HTMLVideoElement>) => {
        onDurationChange?.(e.currentTarget.duration);
    }, [onDurationChange]);


    const moveVideoFrame = (x: number, y: number) => {
        const videoFrameElement = refVideoFrame.current;
        if (videoFrameElement) {
            const MaxX = window.innerWidth - videoFrameElement.getBoundingClientRect().width - OFFSET_LEFT * 2;
            const MaxY = window.innerHeight - videoFrameElement.getBoundingClientRect().height - OFFSET_TOP * 2;

            x = Math.min(Math.max(x, 0), MaxX);
            y = Math.min(Math.max(y, 0), MaxY);

            videoFrameElement.style.top = y + 'px';
            videoFrameElement.style.left = x + 'px';

            setVideoX(x);
            setVideoY(y);
        }
    };

    const handleDraggable = (e: any) => {
        if (zoom) {
            mouseDown = true;
            clickX = e.clientX;
            clickY = e.clientY;
            e.currentTarget.onmouseup = handleMouseUp;
            e.currentTarget.onmousemove = handleMouseMove;
            e.currentTarget.onmouseout = handleMouseUp;
        }
    };

    const handleMouseUp = (e: any) => {
        clickX = 0;
        clickY = 0;
        mouseDown = false;
        e.currentTarget.onmouseup = null;
        e.currentTarget.onmousemove = null;
    };

    const handleMouseMove = (e: any) => {
        if (mouseDown) {
            const boundingRect = e.currentTarget.getBoundingClientRect();
            const tempX = (boundingRect.x + e.clientX - clickX - OFFSET_LEFT - 1);
            const tempY = (boundingRect.y + e.clientY - clickY - OFFSET_TOP - 1);

            moveVideoFrame(tempX, tempY);

            clickX = e.clientX;
            clickY = e.clientY;
        }

        e.preventDefault();
    };

    return (
        <StyledRootDiv className={className}>
            <StyledVideoFrame ref={refVideoFrame}
                              data-zoom={zoom}
                              data-hidden={isBlind}
                              onMouseDown={handleDraggable}>
                {
                    vidoeError ?
                        <StyledDefaultImage src={DEFAULT_VIDEO_IMG} alt={"default-image"}/> :
                        <StyledVideo
                            autoPlay={false}
                            draggable="true"
                            ref={ref}
                            onTimeUpdate={handleTimeUpdate}
                            onDurationChange={handleDurationChange}>
                            <source
                                src={sourceSrc}
                                type={sourceType}
                                onError={(e: any) => {
                                    setVideoError(true);
                                }}/>
                        </StyledVideo>
                }

                <StyledVideoZoomButton onClick={(e: any) => {
                    setZoom(!zoom);

                    const videoFrameElement = refVideoFrame.current;
                    if (videoFrameElement) {
                        videoFrameElement.style.top = '0';
                        videoFrameElement.style.left = '0';
                        if (zoom) {
                            videoFrameElement.style.transition = "all 1s ease 0s";
                            videoFrameElement.ontransitionend = () => {
                                videoFrameElement.style.transition = "";
                            }
                        }
                    }
                }}>
                    <VideoZoomButton zoom={zoom}/>
                </StyledVideoZoomButton>
            </StyledVideoFrame>
            {isBlind && <Blind><span>BLIND</span></Blind>}
        </StyledRootDiv>
    );
});

export default VideoPreview;
